import { db, functions } from "../../firebase";
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";

// Referencia para facturar la comisión de Notiline aparte (ver
// ProductCheckout.js): el pago ya va 100% directo al PayPal del vendedor,
// esto solo sirve para calcular cuánto corresponde facturarle después.
export const STORE_COMMISSION_RATE = 0.05;

const toJsDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Comisión (5% de referencia) todavía no cobrada de esta tienda: suma el
// commissionAmount de las ventas posteriores al último pago de comisión
// registrado (commissionPayments). Sin pagos previos, cuenta todo el
// historial de ventas.
export const getPendingCommission = async (storeSlug, sellerId) => {
  if (!storeSlug || !sellerId) return { amount: 0, salesCount: 0 };
  try {
    const paymentsSnap = await getDocs(
      query(
        collection(db, "commissionPayments"),
        where("storeId", "==", storeSlug),
        where("sellerId", "==", sellerId)
      )
    );

    let lastCoveredUpTo = null;
    paymentsSnap.forEach((docSnap) => {
      const coveredUpTo = toJsDate(docSnap.data().coveredUpTo);
      if (coveredUpTo && (!lastCoveredUpTo || coveredUpTo > lastCoveredUpTo)) {
        lastCoveredUpTo = coveredUpTo;
      }
    });

    const salesSnap = await getDocs(
      query(
        collection(db, "sales"),
        where("storeId", "==", storeSlug),
        where("sellerId", "==", sellerId)
      )
    );

    let amount = 0;
    let salesCount = 0;
    salesSnap.forEach((docSnap) => {
      const sale = docSnap.data();
      const purchaseDate = toJsDate(sale.purchaseDate);
      if (lastCoveredUpTo && purchaseDate && purchaseDate <= lastCoveredUpTo) return;
      amount += Number(sale.commissionAmount) || 0;
      salesCount += 1;
    });

    return { amount, salesCount };
  } catch (error) {
    console.error("Error al calcular la comisión pendiente:", error);
    return { amount: 0, salesCount: 0 };
  }
};

// Registra el pago de la comisión acumulada hasta ahora. No modifica las
// ventas (son de solo lectura por regla de Firestore); en cambio deja un
// registro nuevo que getPendingCommission usa como corte para no volver a
// contar lo ya cobrado. Lo guarda la Cloud Function recordPayPalPayment
// después de verificar el pago con PayPal.
export const markCommissionPaid = async ({ storeSlug, paypalTransactionId }) => {
  await functions.httpsCallable("recordPayPalPayment")({
    kind: "storeCommission",
    targetId: storeSlug,
    orderId: paypalTransactionId,
  });
};

export const postStoreQuestion = async (storeSlug, userId, questionText) => {
  try {
    await addDoc(collection(db, "storeQuestions"), {
      storeId: storeSlug,
      userId,
      question: questionText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la pregunta:", error);
  }
};

export const postStoreAnswer = async (questionId, userId, answerText, storeSlug) => {
  try {
    await addDoc(collection(db, "storeAnswers"), {
      questionId,
      storeId: storeSlug,
      userId,
      answer: answerText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la respuesta:", error);
  }
};

export const fetchStoreQuestionsAndAnswers = (storeSlug, setQuestionsAndAnswers) => {
  const q = query(
    collection(db, "storeQuestions"),
    where("storeId", "==", storeSlug),
    orderBy("createdAt", "asc"),
  );

  const unsubscribe = onSnapshot(q, async (snapshot) => {
    const questionsAndAnswers = [];
    for (const questionDoc of snapshot.docs) {
      const questionData = questionDoc.data();

      let userData = {};
      try {
        const userDocSnap = await getDoc(doc(db, "users", questionData.userId));
        if (userDocSnap.exists()) userData = userDocSnap.data();
      } catch (error) {
        console.error("Error al obtener datos del usuario:", error);
      }

      const answersQuery = query(
        collection(db, "storeAnswers"),
        where("questionId", "==", questionDoc.id),
      );
      const answersSnapshot = await getDocs(answersQuery);
      const answers = await Promise.all(
        answersSnapshot.docs.map(async (answerDoc) => {
          const answerData = answerDoc.data();
          let answerUserData = {};
          try {
            const answerUserDocSnap = await getDoc(doc(db, "users", answerData.userId));
            if (answerUserDocSnap.exists()) answerUserData = answerUserDocSnap.data();
          } catch (error) {
            console.error("Error al obtener datos del usuario:", error);
          }
          return {
            id: answerDoc.id,
            ...answerData,
            displayName: answerUserData.displayName || answerUserData.name || "Usuario",
            profileImageUrl: answerUserData.profileImageUrl || "",
          };
        }),
      );

      questionsAndAnswers.push({
        id: questionDoc.id,
        ...questionData,
        displayName: userData.displayName || userData.name || "Usuario",
        profileImageUrl: userData.profileImageUrl || "",
        answers,
      });
    }

    setQuestionsAndAnswers(questionsAndAnswers);
  });

  return unsubscribe;
};

export const submitStoreRating = async ({
  storeSlug,
  userId,
  displayName,
  profileImageUrl,
  rating,
  comment,
}) => {
  const ratingRef = doc(db, "stores", storeSlug, "ratings", userId);

  // Solo se guarda la calificación propia: el promedio de la tienda
  // (ratingSum/ratingCount/avgRating) lo recalcula la Cloud Function
  // aggregateStoreRatings a partir de todas las calificaciones.
  await runTransaction(db, async (transaction) => {
    const ratingSnap = await transaction.get(ratingRef);

    transaction.set(
      ratingRef,
      {
        userId,
        displayName: displayName || "Usuario",
        profileImageUrl: profileImageUrl || "",
        rating: Math.round(Number(rating)),
        comment: comment || "",
        createdAt: ratingSnap.exists() ? ratingSnap.data().createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });
};

export const fetchStoreRatings = (storeSlug, setRatings) => {
  const q = query(
    collection(db, "stores", storeSlug, "ratings"),
    orderBy("createdAt", "desc"),
  );
  const unsubscribe = onSnapshot(q, (snapshot) => {
    setRatings(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
  });
  return unsubscribe;
};
